import { useFormik } from "formik"
import * as Yup from "yup"
import React, { useMemo, useRef, useState } from "react"
import {
  Card,
  CardBody,
  Label,
  Button,
  Form,
  Input,
  FormFeedback,
  Row,
  Col,
  Table,
  Spinner,
} from "reactstrap"
import CustomHeaderTitle from "components/Common/CustomHeaderTitle"
import CustomSelect from "components/Common/SelectBoxes/CustomSelect"
import { isEmpty, isObject } from "lodash"
import { useDispatch } from "react-redux"
import {
  getProviderItemCategories,
  getProviderItems,
} from "store/provider-dashboard/provider-dashboard-action"
import { providerDashboardAction } from "store/provider-dashboard/provider-dashboard-slice"
import { providerInAction } from "store/provider-in/provider-in-slice"
import Flatpickr from "react-flatpickr"
import moment from "moment"
import Dropzone from "react-dropzone"
import { Link } from "react-router-dom"
import { isArray } from "lodash"
import { getSelectValueWithDefault } from "utils/providerOutUtils"
import { checkForMinusNumber, numberIsSame, removeDocument } from "utils/utils"
import ReceiptsSubmitedList from "./SubmitedList"
import SAlert from "components/Alert"

const ProviderInForm = ({
  onSubmit, // For final submission (Save All)
  onDraftSubmit, // For saving drafts (Submit button)
  onEditSubmit, // For edit mode

  onDeleteDraft, // For deleting single draft
  onDeleteManyDrafts, // For deleting multiple drafts
  loading,
  isDraft = false, // New prop to indicate draft mode
  formMode,
  error,
  warehosues,
  providers,
  commodityClassifications,
  categories,
  items,
  receipt,
  projects,
  fundCodes,
  shipments,
  currencies,
  drafts = [], // Receive drafts from parent
  draftsLoading = false, // Drafts loading state
}) => {
  const [item, setItem] = useState(
    receipt ? items.find(it => it.id === receipt.item_id) : null
  )

  const [selectedFiles, setselectedFiles] = useState({})
  const [draftLoading, setDraftLoading] = useState(false)
  const [localError, setLocalError] = useState({})
  const exipreDateRef = useRef(null)
  const submissionDateRef = useRef(null)
  const [lostQtyError, setLostQtyError] = useState(null)
  const [receiptType, setReceiptType] = useState(
    receipt ? receipt.type : "Receipts"
  )
  const [showOtherSource, setShowOtherSource] = useState(
    receipt ? receipt.type === "Other Source" : false
  )

  // display file name and store file on state
  function handleAcceptedFiles(files) {
    files.map(file =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
        formattedSize: formatBytes(file.size),
      })
    )
    setselectedFiles(files[0])

    // Clear document error when file is uploaded
    if (localError.document) {
      setLocalError(prev => ({ ...prev, document: null }))
    }

    // Clear Redux error state if document error exists
    if (error?.document) {
      const newError = { ...error }
      delete newError.document
      dispatch(
        providerInAction.setError(
          Object.keys(newError).length > 0 ? newError : null
        )
      )
    }
  }

  // change file format to bytes
  function formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const dm = decimals < 0 ? 0 : decimals
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]

    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i]
  }

  const dispatch = useDispatch()

  // Find default currency
  const defaultCurrency = useMemo(() => {
    if (!currencies) return null
    return currencies.find(
      curr => curr.is_default === true || curr.is_default === 1
    )
  }, [currencies])

  // Get default values for select boxes (first option)
  const defaultProject = useMemo(
    () => (formMode === "Add" && projects?.length > 0 ? projects[0] : null),
    [projects, formMode]
  )

  const defaultWarehouse = useMemo(
    () => (formMode === "Add" && warehosues?.length > 0 ? warehosues[0] : null),
    [warehosues, formMode]
  )

  const defaultProvider = useMemo(
    () => (formMode === "Add" && providers?.length > 0 ? providers[0] : null),
    [providers, formMode]
  )

  // Initialize Formik Hook for Validation
  const ReceiptValidation = useFormik({
    enableReinitialize: true,

    initialValues: {
      receipt_number: receipt ? receipt?.receipt_number : "",
      warehouse_id: receipt
        ? receipt?.warehouse_id
        : formMode === "Add" && defaultWarehouse
        ? defaultWarehouse
        : "",
      project_id: receipt
        ? receipt?.project_id
        : formMode === "Add" && defaultProject
        ? defaultProject
        : "",
      fund_code_id: receipt ? receipt?.fund_code_id : "",
      shipment_id: receipt ? receipt?.shipment_id : "",
      waybill_number: receipt ? receipt?.waybill_number : "",
      provider_id: receipt
        ? receipt?.provider_id
        : formMode === "Add" && defaultProvider
        ? defaultProvider
        : "",
      currency_id: receipt
        ? receipt?.currency_id
        : formMode === "Add" && defaultCurrency
        ? defaultCurrency
        : "",
      commodity_classification_id: receipt
        ? receipt?.commodity_classification_id
        : "",
      item_category_id: receipt ? receipt?.item_category_id : "",
      item_id: receipt ? receipt?.item_id : "",
      type: receipt ? receipt?.type : "Receipts",
      other_source: receipt ? receipt?.other_source : "",
      unit_uom_quantity_per_unfpa: receipt
        ? receipt?.unit_uom_quantity_per_unfpa
        : "",
      unit_umo_quantity_per_ip: receipt
        ? receipt?.unit_umo_quantity_per_ip
        : "",
      unit_price_per_unfpa: receipt ? receipt?.unit_price_per_unfpa : "",
      unit_price_per_ip: receipt ? receipt?.unit_price_per_ip : "",
      batch_number: receipt ? receipt?.batch_number : "",
      expire_date: receipt ? receipt?.expire_date : "",
      in_qty_per_unfpa: receipt ? receipt?.in_qty_per_unfpa : "",
      in_qty_per_ip: receipt ? receipt?.in_qty_per_ip : "",
      lost_quantity: receipt ? receipt?.lost_quantity : 0,
      lost_comment: receipt ? receipt?.lost_comment : "",
      total_value: receipt ? receipt?.total_value : "",
      date: receipt ? receipt?.date : "",
    },
    validationSchema: Yup.object().shape({
      item_category_id: formMode === "Add" ? Yup.object().nullable() : "",
      item_id: formMode === "Add" ? Yup.object().nullable() : "",
      unit_price_per_unfpa: Yup.number().required(
        "Type Unit Price is required"
      ),
      unit_price_per_ip: Yup.string().required(
        "Type Unit Price (as per IP) is required"
      ),
      batch_number: Yup.string().required("Batch Number is required"),
      receipt_number: Yup.string().required("Receipt Number is required"),
      expire_date: Yup.string().required("Expire Date is required"),
      in_qty_per_unfpa: Yup.number().required(
        "In Quantity (Quantity UNFPA) is required"
      ),
      in_qty_per_ip: Yup.number().required("Quantity (IP UOM) is required"),
      total_value: Yup.number().required("Total Value is required"),
      date: Yup.string().required("Submission Date is required"),
      shipment_id: Yup.mixed().required("Shipment is required"),
      waybill_number: Yup.string().when("shipment_id", {
        is: value => {
          if (!value) return false
          return isObject(value) ? !!value.value : !!value
        },
        then: schema => schema.required("Waybill Number is required"),
        otherwise: schema => schema.notRequired(),
      }),
    }),

    onSubmit: async values => {
      // check lost qty is not greater than qty
      if (values.lost_quantity > values.in_qty_per_ip) {
        setLostQtyError("Lost quantity cannot be greater than quantity")
        return
      }

      // Create a copy of values for submission
      const submissionValues = { ...values }

      // Convert select objects to IDs for backend submission
      submissionValues.warehouse_id = getSelectValueWithDefault(
        submissionValues.warehouse_id,
        warehosues
      )
      submissionValues.provider_id = getSelectValueWithDefault(
        submissionValues.provider_id,
        providers
      )
      submissionValues.commodity_classification_id = getSelectValueWithDefault(
        submissionValues.commodity_classification_id,
        commodityClassifications
      )
      submissionValues.item_category_id = getSelectValueWithDefault(
        submissionValues.item_category_id,
        categories
      )
      submissionValues.item_id = getSelectValueWithDefault(
        submissionValues.item_id,
        filteredItems
      )
      submissionValues.project_id = getSelectValueWithDefault(
        submissionValues.project_id,
        projects
      )
      submissionValues.fund_code_id = getSelectValueWithDefault(
        submissionValues.fund_code_id,
        fundCodes
      )
      submissionValues.shipment_id = getSelectValueWithDefault(
        submissionValues.shipment_id,
        shipments
      )
      if (!submissionValues.shipment_id) {
        submissionValues.waybill_number = ""
      }
      submissionValues.currency_id = getSelectValueWithDefault(
        submissionValues.currency_id,
        currencies
      )

      if (formMode === "Add") {
        // extract lost qty from qty and update submission values
        QuantityWithLostQuantity(submissionValues)

        // Add status for draft
        submissionValues.status = "draft"

        // Save as draft to server
        setDraftLoading(true)
        try {
          await onSubmit(submissionValues, selectedFiles)
        } catch (error) {
          // SAlert({ text: "Failed to save draft", status: "error" })
        } finally {
          setDraftLoading(false)
        }

        // Reset form for next entry - keep preserved fields as objects
        resetAllForm()
      } else {
        // Edit mode logic
        if (receipt?.in_qty_per_ip !== submissionValues?.in_qty_per_ip) {
          QuantityWithLostQuantity(submissionValues)
        }
        if (receipt?.lost_quantity !== submissionValues?.lost_quantity) {
          submissionValues.in_qty_per_ip =
            receipt?.in_qty_per_ip + receipt?.lost_quantity
          QuantityWithLostQuantity(submissionValues)
        }

        await onSubmit(submissionValues, selectedFiles)
      }
    },
  })

  // extract lost qty from qty and update values
  function QuantityWithLostQuantity(values) {
    let quantity_per_ip = values.in_qty_per_ip - values.lost_quantity
    let unfpa_quantity
    if (values.unit_uom_quantity_per_unfpa != values.unit_umo_quantity_per_ip) {
      unfpa_quantity = quantity_per_ip / values.unit_uom_quantity_per_unfpa
    } else {
      unfpa_quantity = quantity_per_ip
    }
    const total_value = parseFloat(values.unit_price_per_unfpa) * unfpa_quantity
    values.in_qty_per_ip = quantity_per_ip
    values.in_qty_per_unfpa = unfpa_quantity
    values.total_value = total_value
  }

  // Final submission - submit all drafts
  const saveAllHandler = () => {
    if (drafts && drafts.length > 0) {
      onDraftSubmit()
    } else {
      SAlert({ text: "No draft items to submit", status: "info" })
    }
  }

  // reset form selectively - preserve Projects through Items, only reset Unit Price through Total Value
  const resetAllForm = () => {
    // Only clear expire date, keep submission date
    exipreDateRef.current.flatpickr.clear()

    // Reset only the fields from "Receipt Number" to "Total Value"
    // Keep everything else (Projects, Currency, Warehouse, Providers, Item Categories, Items, Type, Submission Date)
    ReceiptValidation.setFieldValue("receipt_number", "")
    ReceiptValidation.setFieldValue("unit_price_per_unfpa", "")
    ReceiptValidation.setFieldValue("unit_price_per_ip", "")
    ReceiptValidation.setFieldValue("batch_number", "")
    ReceiptValidation.setFieldValue("in_qty_per_ip", "")
    ReceiptValidation.setFieldValue("in_qty_per_unfpa", "")
    ReceiptValidation.setFieldValue("lost_comment", "")
    ReceiptValidation.setFieldValue("lost_quantity", "")
    ReceiptValidation.setFieldValue("total_value", "")
    ReceiptValidation.setFieldValue("expire_date", "")

    // Clear all errors and touched state
    ReceiptValidation.setErrors({})
    ReceiptValidation.setTouched({})

    // Clear validation errors for preserved fields
    const clearPreservedFieldErrors = () => {
      const currentErrors = { ...ReceiptValidation.errors }
      const preservedFields = [
        "commodity_classification_id",
        "item_category_id",
        "item_id",
        "project_id",
        "fund_code_id",
        "shipment_id",
        "waybill_number",
        "warehouse_id",
        "provider_id",
        "date",
        "type",
      ]

      preservedFields.forEach(field => {
        if (ReceiptValidation.values[field]) {
          delete currentErrors[field]
        }
      })

      ReceiptValidation.setErrors(currentErrors)
    }

    // Clear errors with slight delay to ensure form state is updated
    setTimeout(clearPreservedFieldErrors, 50)

    // Reset file selection and errors
    setselectedFiles({})
    setLostQtyError(null)

    // Reset local errors only for fields being reset
    const resetFields = [
      "receipt_number",
      "unit_price_per_unfpa",
      "unit_price_per_ip",
      "batch_number",
      "in_qty_per_ip",
      "in_qty_per_unfpa",
      "lost_comment",
      "lost_quantity",
      "total_value",
      "expire_date",
      "document",
    ]

    const newLocalError = { ...localError }
    resetFields.forEach(field => delete newLocalError[field])
    setLocalError(newLocalError)

    // Clear Redux errors for preserved fields
    if (error) {
      const newError = { ...error }
      const preservedFields = [
        "commodity_classification_id",
        "item_category_id",
        "item_id",
        "project_id",
        "fund_code_id",
        "shipment_id",
        "waybill_number",
        "warehouse_id",
        "provider_id",
        "date",
        "type",
      ]

      preservedFields.forEach(field => {
        if (ReceiptValidation.values[field] && newError[field]) {
          delete newError[field]
        }
      })

      dispatch(
        providerInAction.setError(
          Object.keys(newError).length > 0 ? newError : null
        )
      )
    }
  }

  // reset all bottom of change items when item change
  const resetFormAfterItemChanged = () => {
    ReceiptValidation.setFieldValue("unit_price_per_unfpa", "")
    ReceiptValidation.setFieldValue("unit_price_per_ip", "")
    ReceiptValidation.setFieldValue("batch_number", "")
    ReceiptValidation.setFieldValue("in_qty_per_ip", "")
    ReceiptValidation.setFieldValue("in_qty_per_unfpa", "")
    ReceiptValidation.setFieldValue("lost_comment", "")
    ReceiptValidation.setFieldValue("lost_quantity", "")
    ReceiptValidation.setFieldValue("total_value", "")
  }

  // Store warehouse value - keep as object for preservation
  const onWarehouseChangeHander = value => {
    ReceiptValidation.setFieldValue("warehouse_id", value)
  }

  // Store project value - keep as object for preservation
  const onProjectChangeHandler = value => {
    ReceiptValidation.setFieldValue("project_id", value)
  }

  const onFundCodeChangeHandler = value => {
    ReceiptValidation.setFieldValue("fund_code_id", value)
  }

  const onShipmentChangeHandler = value => {
    ReceiptValidation.setFieldValue("shipment_id", value)
    if (!value) {
      ReceiptValidation.setFieldValue("waybill_number", "")
    }
  }

  const selectedShipmentId = isObject(ReceiptValidation.values.shipment_id)
    ? ReceiptValidation.values.shipment_id?.value
    : ReceiptValidation.values.shipment_id

  const showWaybillNumber = !!selectedShipmentId

  // Store currency value - keep as object for preservation
  const onCurrencyChangeHandler = value => {
    ReceiptValidation.setFieldValue("currency_id", value)
  }

  // Store provider value - keep as object for preservation
  const onProviderChangeHandler = value => {
    ReceiptValidation.setFieldValue("provider_id", value)
  }

  const onCommodityClassificationChangeHandler = value => {
    ReceiptValidation.setFieldValue("commodity_classification_id", value)
    ReceiptValidation.setFieldValue("item_category_id", null)
    ReceiptValidation.setFieldValue("item_id", null)
    setItem(null)
    dispatch(providerInAction.setError(null))
    dispatch(providerDashboardAction.resetProviderItems())
    if (value?.value) {
      dispatch(getProviderItemCategories(value.value))
    } else {
      dispatch(providerDashboardAction.resetProviderItemCategories())
    }
  }

  // store item category value when select from form and fetch new items based item category
  const onItemCategoryChangeHandler = value => {
    ReceiptValidation.setFieldValue("item_category_id", value)
    dispatch(getProviderItems(value.value))
    ReceiptValidation.setFieldValue("item_id", null)
    setItem(null)
  }

  // store item value when select from form
  const onItemChangeHandler = value => {
    resetFormAfterItemChanged()
    ReceiptValidation.setFieldValue("item_id", value)
    const data = items.find(item => item.id === value.value)
    setItem(data)
    ReceiptValidation.setFieldValue(
      "unit_uom_quantity_per_unfpa",
      data.unfpa_uom_quantity
    )
    ReceiptValidation.setFieldValue(
      "unit_umo_quantity_per_ip",
      data.ip_uom_quantity
    )
  }

  // Handle type change (Receipt or Other Source)
  const onTypeChangeHandler = value => {
    setReceiptType(value)
    ReceiptValidation.setFieldValue("type", value)

    if (value === "Other Source") {
      setShowOtherSource(true)
    } else {
      setShowOtherSource(false)
      ReceiptValidation.setFieldValue("other_source", "")
    }
  }

  // check to not insert minus and also format unfpa qty and ip quty and balacne based formula
  const onUNFPAKeyUp = value => {
    // helper function to check number don't go to minus
    if (checkForMinusNumber(value)) {
      ReceiptValidation.setFieldValue("unit_price_per_unfpa", 0)
      return
    }
    const unfpa_quantity = parseFloat(
      ReceiptValidation.values.unit_uom_quantity_per_unfpa
    )
    const ip_quantity = parseFloat(
      ReceiptValidation.values.unit_umo_quantity_per_ip
    )

    const unfpa_price = parseFloat(
      ReceiptValidation.values.unit_price_per_unfpa
    )
    // helper function check number is same
    if (!numberIsSame(ip_quantity, unfpa_quantity)) {
      const price_per_ip = unfpa_price / unfpa_quantity
      ReceiptValidation.setFieldValue(
        "unit_price_per_ip",
        price_per_ip.toFixed(2)
      )
    } else {
      ReceiptValidation.setFieldValue("unit_price_per_ip", unfpa_price)
    }
  }

  // helper function do disable some inputs when we edit it.
  const disableField = ({ isformMode }) => {
    return (
      receipt?.countDistribution > 0 || (isformMode ? formMode === "Edit" : "")
    )
  }

  // store in qty per ip and total value based formula when UNFPA Qty input key up
  const onUNFPAQuantityChange = e => {
    // helper function for check minus from number
    if (checkForMinusNumber(e)) {
      ReceiptValidation.setFieldValue("in_qty_per_unfpa", 0)
      return
    }
    let quantity_per_ip
    if (
      ReceiptValidation.values.unit_uom_quantity_per_unfpa !=
      ReceiptValidation.values.unit_umo_quantity_per_ip
    ) {
      quantity_per_ip =
        ReceiptValidation.values.in_qty_per_unfpa *
        ReceiptValidation.values.unit_uom_quantity_per_unfpa
    } else {
      quantity_per_ip = ReceiptValidation.values.in_qty_per_unfpa
    }
    const total_value =
      parseFloat(ReceiptValidation.values.unit_price_per_unfpa) *
      parseFloat(ReceiptValidation.values.in_qty_per_unfpa)
    ReceiptValidation.setFieldValue("in_qty_per_ip", quantity_per_ip)
    ReceiptValidation.setFieldValue("total_value", total_value)
  }

  const filteredItems = useMemo(
    () =>
      items.map(item => ({
        value: item.id,
        label: `${item.unfp_item_name} ( ${item.item_name} )`,
      })),
    [items]
  )

  // Group drafts by item_id
  const groupedDrafts = useMemo(() => {
    if (!drafts || drafts.length === 0) return []

    const groups = drafts.reduce((acc, draft) => {
      const itemId = draft.item_id
      if (!acc[itemId]) {
        acc[itemId] = {
          item_id: itemId,
          item_name:
            draft.item?.unfp_item_name ||
            draft.item?.item_name ||
            "Unknown Item",
          ip_item_description: draft.item?.ip_item_description || "N/A",
          drafts: [],
        }
      }
      acc[itemId].drafts.push(draft)
      return acc
    }, {})

    // Convert object to array
    return Object.values(groups)
  }, [drafts])

  return (
    <Card>
      <CardBody>
        <CustomHeaderTitle
          loading={loading}
          title={formMode === "Edit" ? "Edit Receipt" : "Add Receipt"}
          lgColumn={4}
          rowCount={false}
          orderingList={false}
          searchBox={false}
          rightBtn={true}
          rightBtnTitle={"Back"}
          rightBtnRoute={"/provider-receipts"}
        />

        <Form
          onSubmit={async e => {
            e.preventDefault()

            // For draft submission in Add mode, check minimum requirements
            if (formMode === "Add") {
              // Check if category and item are selected for draft submission
              const hasClassification =
                ReceiptValidation.values.commodity_classification_id
              const hasCategory = ReceiptValidation.values.item_category_id
              const hasItem = ReceiptValidation.values.item_id

              if (hasClassification && hasCategory && hasItem) {
                // For draft submission, we only require category and item selection
                // Clear any existing validation errors for category and item fields
                const currentErrors = { ...ReceiptValidation.errors }
                delete currentErrors.commodity_classification_id
                delete currentErrors.item_category_id
                delete currentErrors.item_id
                ReceiptValidation.setErrors(currentErrors)

                // Proceed with draft submission
                ReceiptValidation.submitForm()
                return false
              } else {
                // If category or item not selected, show specific error
                const missingFields = {}
                if (!hasClassification) {
                  missingFields.commodity_classification_id =
                    "Unit Classification is required for draft"
                }
                if (!hasCategory) {
                  missingFields.item_category_id =
                    "Item category is required for draft"
                }
                if (!hasItem) {
                  missingFields.item_id = "Item is required for draft"
                }

                ReceiptValidation.setErrors(missingFields)
                ReceiptValidation.setTouched({
                  commodity_classification_id: !hasClassification,
                  item_category_id: !hasCategory,
                  item_id: !hasItem,
                })
                return false
              }
            }

            // For edit mode or when category/item not selected, do full validation
            const errors = await ReceiptValidation.validateForm()

            // Remove validation errors for preserved fields that have values
            const cleanedErrors = { ...errors }

            if (ReceiptValidation.values.commodity_classification_id) {
              delete cleanedErrors.commodity_classification_id
            }
            if (ReceiptValidation.values.item_category_id) {
              delete cleanedErrors.item_category_id
            }
            if (ReceiptValidation.values.item_id) {
              delete cleanedErrors.item_id
            }
            if (ReceiptValidation.values.project_id) {
              delete cleanedErrors.project_id
            }
            if (ReceiptValidation.values.fund_code_id) {
              delete cleanedErrors.fund_code_id
            }
            if (ReceiptValidation.values.shipment_id) {
              delete cleanedErrors.shipment_id
            }
            if (ReceiptValidation.values.waybill_number) {
              delete cleanedErrors.waybill_number
            }
            if (ReceiptValidation.values.currency_id) {
              delete cleanedErrors.currency_id
            }
            if (ReceiptValidation.values.warehouse_id) {
              delete cleanedErrors.warehouse_id
            }
            if (ReceiptValidation.values.provider_id) {
              delete cleanedErrors.provider_id
            }
            if (ReceiptValidation.values.date) {
              delete cleanedErrors.date
            }

            // Set the cleaned errors
            ReceiptValidation.setErrors(cleanedErrors)

            // If there are no errors after cleaning, submit the form
            if (Object.keys(cleanedErrors).length === 0) {
              ReceiptValidation.submitForm()
            } else {
              // Mark all fields as touched to show validation errors
              ReceiptValidation.setTouched(
                Object.keys(cleanedErrors).reduce((acc, key) => {
                  acc[key] = true
                  return acc
                }, {})
              )
            }

            return false
          }}
        >
          {/* file upload library  */}
          <Dropzone
            onDrop={acceptedFiles => {
              handleAcceptedFiles(acceptedFiles)
            }}
            useFsAccessApi={false}
            multiple={false}
          >
            {({ getRootProps, getInputProps }) => (
              <div className="dropzone">
                <div className="dz-message needsclick mt-2" {...getRootProps()}>
                  <input {...getInputProps()} />
                  <div className="mb-3">
                    <i className="display-4 text-muted bx bxs-cloud-upload" />
                  </div>
                  <h4>Drop files here or click to upload.</h4>
                </div>
              </div>
            )}
          </Dropzone>
          <div className="dropzone-previews mt-3" id="file-previews">
            <Card className="mt-1 mb-0 shadow-none border dz-processing dz-image-preview dz-success dz-complete file">
              <div className="p-2">
                <Row className="align-items-center">
                  <Col>
                    <Link to="#" className="text-muted font-weight-bold">
                      {selectedFiles.name}
                    </Link>
                    <p className="mb-0">
                      <strong>{selectedFiles.formattedSize}</strong>
                    </p>
                    {!isEmpty(selectedFiles) && (
                      <Button
                        className="btn btn-sm btn-danger"
                        onClick={() => {
                          removeDocument(setselectedFiles)
                        }}
                      >
                        Remove
                      </Button>
                    )}
                  </Col>
                </Row>
              </div>
            </Card>
            {(error?.document?.[0] || localError?.document) && (
              <p className="text-danger">
                {localError?.document || error?.document?.[0]}
              </p>
            )}
          </div>

          <div className="mb-3">
            <Row>
              <Col>
                <Label className="form-label">Projects</Label>
                {projects && (
                  <CustomSelect
                    name="project_id"
                    options={projects}
                    isDisabled={disableField({})}
                    value={
                      isObject(ReceiptValidation.values.project_id)
                        ? ReceiptValidation.values.project_id
                        : projects.find(
                            pro =>
                              ReceiptValidation.values.project_id === pro.value
                          )
                    }
                    onChange={onProjectChangeHandler}
                  />
                )}
                {error?.project_id?.[0] && (
                  <p className="text-danger p-1">{error?.project_id?.[0]}</p>
                )}
              </Col>
              <Col>
                <Label className="form-label">Currency</Label>
                {currencies && (
                  <CustomSelect
                    name="currency_id"
                    options={currencies}
                    isDisabled={disableField({})}
                    value={
                      isObject(ReceiptValidation.values.currency_id)
                        ? ReceiptValidation.values.currency_id
                        : currencies.find(
                            curr =>
                              ReceiptValidation.values.currency_id ===
                              curr.value
                          ) || (formMode === "Add" ? defaultCurrency : null)
                    }
                    onChange={onCurrencyChangeHandler}
                  />
                )}
                {error?.currency_id?.[0] && (
                  <p className="text-danger p-1">{error?.currency_id?.[0]}</p>
                )}
              </Col>
            </Row>
          </div>

          <div className="mb-3">
            <Row>
              <Col>
                <Label className="form-label">Fund Code</Label>
                {fundCodes && (
                  <CustomSelect
                    name="fund_code_id"
                    options={fundCodes}
                    isDisabled={disableField({})}
                    value={
                      isObject(ReceiptValidation.values.fund_code_id)
                        ? ReceiptValidation.values.fund_code_id
                        : fundCodes.find(
                            fundCode =>
                              ReceiptValidation.values.fund_code_id ===
                              fundCode.value
                          )
                    }
                    onChange={onFundCodeChangeHandler}
                  />
                )}
                {error?.fund_code_id?.[0] && (
                  <p className="text-danger p-1">{error?.fund_code_id?.[0]}</p>
                )}
              </Col>
            </Row>
          </div>

          <div className="mb-3">
            <Row>
              <Col>
                <Label className="form-label">Shipment</Label>
                {shipments && (
                  <CustomSelect
                    name="shipment_id"
                    options={shipments}
                    isDisabled={disableField({})}
                    value={
                      isObject(ReceiptValidation.values.shipment_id)
                        ? ReceiptValidation.values.shipment_id
                        : shipments.find(
                            shipment =>
                              ReceiptValidation.values.shipment_id ===
                              shipment.value
                          )
                    }
                    onChange={onShipmentChangeHandler}
                  />
                )}
                {error?.shipment_id?.[0] && (
                  <p className="text-danger p-1">{error?.shipment_id?.[0]}</p>
                )}
              </Col>
              {showWaybillNumber && (
                <Col>
                  <Label className="form-label">Waybill Number</Label>
                  <Input
                    name="waybill_number"
                    placeholder="Waybill Number"
                    type="text"
                    onChange={ReceiptValidation.handleChange}
                    onBlur={ReceiptValidation.handleBlur}
                    value={ReceiptValidation.values.waybill_number || ""}
                    invalid={
                      (ReceiptValidation.touched.waybill_number &&
                        ReceiptValidation.errors.waybill_number) ||
                      error?.waybill_number?.[0]
                        ? true
                        : false
                    }
                  />
                  {(ReceiptValidation.touched.waybill_number &&
                    ReceiptValidation.errors.waybill_number) ||
                  error?.waybill_number?.[0] ? (
                    <FormFeedback type="invalid">
                      {ReceiptValidation.errors.waybill_number ||
                        error?.waybill_number?.[0]}
                    </FormFeedback>
                  ) : null}
                </Col>
              )}
            </Row>
          </div>

          <div className="mb-3">
            <Row>
              <Col>
                <Label className="form-label">Warehouses</Label>
                {warehosues && (
                  <CustomSelect
                    name="warehouse_id"
                    options={warehosues}
                    isDisabled={disableField({})}
                    value={
                      isObject(ReceiptValidation.values.warehouse_id)
                        ? ReceiptValidation.values.warehouse_id
                        : warehosues.find(
                            ware =>
                              ReceiptValidation.values.warehouse_id ===
                              ware.value
                          )
                    }
                    onChange={onWarehouseChangeHander}
                  />
                )}
                {error?.warehouse_id?.[0] && (
                  <p className="text-danger p-1">{error?.warehouse_id?.[0]}</p>
                )}
              </Col>
              <Col>
                <Label className="form-label">Providers</Label>
                {providers && (
                  <CustomSelect
                    name="provider_id"
                    options={providers}
                    isDisabled={disableField({})}
                    value={
                      isObject(ReceiptValidation.values.provider_id)
                        ? ReceiptValidation.values.provider_id
                        : providers.find(
                            prov =>
                              ReceiptValidation.values.provider_id ===
                              prov.value
                          )
                    }
                    onChange={onProviderChangeHandler}
                  />
                )}
                {error?.provider_id?.[0] && (
                  <p className="text-danger p-1">{error?.provider_id?.[0]}</p>
                )}
              </Col>
            </Row>
          </div>

          <div className="mb-3">
            <Row>
              <Col>
                <Label className="form-label">Unit Classification</Label>
                {commodityClassifications && (
                  <CustomSelect
                    name="commodity_classification_id"
                    options={commodityClassifications}
                    isDisabled={disableField({ isformMode: true })}
                    value={
                      isObject(
                        ReceiptValidation.values.commodity_classification_id
                      )
                        ? ReceiptValidation.values.commodity_classification_id
                        : commodityClassifications.find(
                            option =>
                              ReceiptValidation.values
                                .commodity_classification_id === option.value
                          )
                    }
                    onChange={onCommodityClassificationChangeHandler}
                  />
                )}
                {(ReceiptValidation.touched.commodity_classification_id &&
                  ReceiptValidation.errors.commodity_classification_id) ||
                error?.commodity_classification_id ? (
                  <p className="text-danger" style={{ fontSize: "80%" }}>
                    {ReceiptValidation.errors.commodity_classification_id ||
                      error?.commodity_classification_id}
                  </p>
                ) : null}
              </Col>
              <Col>
                <Label className="form-label">Item Categories</Label>
                {categories && (
                  <CustomSelect
                    name="item_category_id"
                    options={categories}
                    isDisabled={
                      disableField({ isformMode: true }) ||
                      !ReceiptValidation.values.commodity_classification_id
                    }
                    value={
                      isObject(ReceiptValidation.values.item_category_id)
                        ? ReceiptValidation.values.item_category_id
                        : categories.find(
                            cate =>
                              ReceiptValidation.values.item_category_id ===
                              cate.value
                          )
                    }
                    onChange={onItemCategoryChangeHandler}
                  />
                )}
                {(ReceiptValidation.touched.item_category_id &&
                  ReceiptValidation.errors.item_category_id) ||
                error?.item_category_id ? (
                  <p className="text-danger" style={{ fontSize: "80%" }}>
                    {ReceiptValidation.errors.item_category_id ||
                      error?.item_category_id}
                  </p>
                ) : null}
              </Col>
              <Col>
                <Label className="form-label">Items</Label>
                {filteredItems && (
                  <CustomSelect
                    name="item_id"
                    options={filteredItems}
                    isDisabled={disableField({ isformMode: true })}
                    value={
                      isObject(ReceiptValidation.values.item_id)
                        ? ReceiptValidation.values.item_id
                        : filteredItems.find(
                            item =>
                              ReceiptValidation.values.item_id === item.value
                          )
                    }
                    onChange={onItemChangeHandler}
                  />
                )}
                {(ReceiptValidation.touched.item_id &&
                  ReceiptValidation.errors.item_id) ||
                error?.item_id ? (
                  <p className="text-danger" style={{ fontSize: "80%" }}>
                    {ReceiptValidation.errors.item_id || error?.item_id}
                  </p>
                ) : null}
              </Col>
            </Row>
          </div>

          <div className="mb-3">
            <Row>
              <Col>
                <Label className="form-label">Type</Label>
                <CustomSelect
                  name="type"
                  options={[
                    { value: "Receipts", label: "Receipts" },
                    { value: "Other Source", label: "Other Source" },
                  ]}
                  isDisabled={disableField({})}
                  value={{
                    value: receiptType,
                    label: receiptType,
                  }}
                  onChange={selected => onTypeChangeHandler(selected.value)}
                />
              </Col>
              {showOtherSource && (
                <Col>
                  <Label className="form-label">
                    Other Source Name <span className="text-danger">*</span>
                  </Label>
                  <Input
                    name="other_source"
                    placeholder="Enter source name"
                    type="text"
                    onChange={ReceiptValidation.handleChange}
                    onBlur={ReceiptValidation.handleBlur}
                    value={ReceiptValidation.values.other_source || ""}
                    disabled={disableField({})}
                  />
                  {ReceiptValidation.touched.other_source &&
                    ReceiptValidation.errors.other_source && (
                      <p className="text-danger" style={{ fontSize: "80%" }}>
                        {ReceiptValidation.errors.other_source}
                      </p>
                    )}
                </Col>
              )}
            </Row>
          </div>

          {item && (
            <div className="mb-3">
              <Table>
                <thead>
                  <tr>
                    <th>UNFPA ITEM ID</th>
                    <th>IP-ITEM DESCRIPTION</th>
                    <th>UNFPA UOM</th>
                    <th>IP UNIT UOM</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{item?.unfp_item_name}</td>
                    <td>{item?.ip_item_description}</td>
                    <td>
                      {item?.uom}{" "}
                      {item?.unfpa_uom_quantity > 1 && item?.unfpa_uom_quantity}
                    </td>
                    <td>
                      {item?.ip_unit_umo}{" "}
                      {item?.ip_uom_quantity > 1 && item?.ip_uom_quantity}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </div>
          )}

          <Row>
            <Col>
              <div className="mb-3">
                <Label className="form-label">
                  Type Unit Price ( as per UNFPA ){" "}
                  <span className="text-danger">*</span>
                </Label>
                <Input
                  name="unit_price_per_unfpa"
                  placeholder=" Type Unit Price ( as per UNFPA )"
                  type="number"
                  onWheel={e => e.target.blur()}
                  onChange={ReceiptValidation.handleChange}
                  disabled={disableField({})}
                  onBlur={ReceiptValidation.handleBlur}
                  value={ReceiptValidation.values.unit_price_per_unfpa}
                  onKeyUp={onUNFPAKeyUp}
                  invalid={
                    (ReceiptValidation.touched.unit_price_per_unfpa &&
                      ReceiptValidation.errors.unit_price_per_unfpa) ||
                    error?.unit_price_per_unfpa?.[0]
                      ? true
                      : false
                  }
                />
                {(ReceiptValidation.touched.unit_price_per_unfpa &&
                  ReceiptValidation.errors.unit_price_per_unfpa) ||
                error?.unit_price_per_unfpa?.[0] ? (
                  <FormFeedback type="invalid">
                    {ReceiptValidation.errors.unit_price_per_unfpa ||
                      error?.unit_price_per_unfpa?.[0]}
                  </FormFeedback>
                ) : null}
              </div>
            </Col>
            <Col>
              <div className="mb-3">
                <Label className="form-label">
                  Type Unit Price ( as per IP ){" "}
                  <span className="text-danger">*</span>
                </Label>
                <Input
                  name="unit_price_per_ip"
                  placeholder=" Type Unit Price ( as per IP )"
                  type="number"
                  onWheel={e => e.target.blur()}
                  onChange={ReceiptValidation.handleChange}
                  onBlur={ReceiptValidation.handleBlur}
                  value={ReceiptValidation.values.unit_price_per_ip || ""}
                  invalid={
                    (ReceiptValidation.touched.unit_price_per_ip &&
                      ReceiptValidation.errors.unit_price_per_ip) ||
                    error?.unit_price_per_ip?.[0]
                      ? true
                      : false
                  }
                  readOnly={true}
                />
                {(ReceiptValidation.touched.unit_price_per_ip &&
                  ReceiptValidation.errors.unit_price_per_ip) ||
                error?.unit_price_per_ip?.[0] ? (
                  <FormFeedback type="invalid">
                    {ReceiptValidation.errors.unit_price_per_ip ||
                      error?.unit_price_per_ip?.[0]}
                  </FormFeedback>
                ) : null}
              </div>
            </Col>
          </Row>

          <Row>
            <Col>
              <div className="mb-3">
                <Label className="form-label">
                  Batch Number <span className="text-danger">*</span>
                </Label>
                <Input
                  name="batch_number"
                  placeholder=" Batch Number"
                  type="text"
                  disabled={disableField({})}
                  onChange={ReceiptValidation.handleChange}
                  onBlur={ReceiptValidation.handleBlur}
                  value={ReceiptValidation.values.batch_number || ""}
                  invalid={
                    (ReceiptValidation.touched.batch_number &&
                      ReceiptValidation.errors.batch_number) ||
                    error?.batch_number?.[0]
                      ? true
                      : false
                  }
                />
                {(ReceiptValidation.touched.batch_number &&
                  ReceiptValidation.errors.batch_number) ||
                error?.batch_number?.[0] ? (
                  <FormFeedback type="invalid">
                    {ReceiptValidation.errors.batch_number ||
                      error?.batch_number?.[0]}
                  </FormFeedback>
                ) : null}
              </div>
            </Col>
            <Col>
              <div className="mb-3">
                <Label className="form-label">
                  DS Number <span className="text-danger">*</span>
                </Label>
                <Input
                  name="receipt_number"
                  placeholder="Enter DS Number "
                  type="text"
                  disabled={disableField({})}
                  onChange={ReceiptValidation.handleChange}
                  onBlur={ReceiptValidation.handleBlur}
                  value={ReceiptValidation.values.receipt_number || ""}
                  invalid={
                    (ReceiptValidation.touched.receipt_number &&
                      ReceiptValidation.errors.receipt_number) ||
                    error?.receipt_number?.[0]
                      ? true
                      : false
                  }
                />
                {(ReceiptValidation.touched.receipt_number &&
                  ReceiptValidation.errors.receipt_number) ||
                error?.receipt_number?.[0] ? (
                  <FormFeedback type="invalid">
                    {ReceiptValidation.errors.receipt_number ||
                      error?.receipt_number?.[0]}
                  </FormFeedback>
                ) : null}
              </div>
            </Col>
            <Col>
              <div className="mb-3">
                <Label className="form-label">
                  Expire Date <span className="text-danger">*</span>
                </Label>
                <Flatpickr
                  ref={exipreDateRef}
                  className="form-control d-block flatpickr-disabled"
                  placeholder="dd M,yyyy"
                  disabled={disableField({})}
                  options={{
                    altInput: true,
                    altFormat: "Y-m-d",
                    dateFormat: "Y-m-d",
                    defaultDate: ReceiptValidation.values.expire_date,
                  }}
                  onChange={value => {
                    ReceiptValidation.setFieldValue(
                      "expire_date",
                      moment(value[0]).format("YYYY-MM-DD")
                    )
                  }}
                />
                {(ReceiptValidation.touched.expire_date &&
                  ReceiptValidation.errors.expire_date) ||
                error?.expire_date ? (
                  <p className="text-danger" style={{ fontSize: "80%" }}>
                    {ReceiptValidation.errors.expire_date || error?.expire_date}
                  </p>
                ) : null}
              </div>
            </Col>
          </Row>

          <Row>
            <Col>
              <div className="mb-3">
                <Label className="form-label">
                  In Quantity (Quantity UNFPA){" "}
                  <span className="text-danger">*</span>
                </Label>
                <Input
                  name="in_qty_per_unfpa"
                  placeholder=" In Quantity (Quantity UNFPA)"
                  type="number"
                  onWheel={e => e.target.blur()}
                  disabled={disableField({})}
                  onChange={ReceiptValidation.handleChange}
                  onBlur={ReceiptValidation.handleBlur}
                  value={ReceiptValidation.values.in_qty_per_unfpa || ""}
                  onKeyUp={onUNFPAQuantityChange}
                  invalid={
                    (ReceiptValidation.touched.in_qty_per_unfpa &&
                      ReceiptValidation.errors.in_qty_per_unfpa) ||
                    error?.in_qty_per_unfpa?.[0]
                      ? true
                      : false
                  }
                />
                {(ReceiptValidation.touched.in_qty_per_unfpa &&
                  ReceiptValidation.errors.in_qty_per_unfpa) ||
                error?.in_qty_per_unfpa?.[0] ? (
                  <FormFeedback type="invalid">
                    {ReceiptValidation.errors.in_qty_per_unfpa ||
                      error?.in_qty_per_unfpa?.[0]}
                  </FormFeedback>
                ) : null}
              </div>
            </Col>
            <Col>
              <div className="mb-3">
                <Label className="form-label">
                  Quantity (IP UOM) <span className="text-danger">*</span>
                </Label>
                <Input
                  name="in_qty_per_ip"
                  placeholder=" Quantity (IP UOM)"
                  type="number"
                  min={0}
                  onWheel={e => e.target.blur()}
                  onChange={ReceiptValidation.handleChange}
                  onBlur={ReceiptValidation.handleBlur}
                  value={ReceiptValidation.values.in_qty_per_ip || ""}
                  invalid={
                    (ReceiptValidation.touched.in_qty_per_ip &&
                      ReceiptValidation.errors.in_qty_per_ip) ||
                    error?.in_qty_per_ip?.[0]
                      ? true
                      : false
                  }
                  readOnly={true}
                />
                {(ReceiptValidation.touched.in_qty_per_ip &&
                  ReceiptValidation.errors.in_qty_per_ip) ||
                error?.in_qty_per_ip?.[0] ? (
                  <FormFeedback type="invalid">
                    {ReceiptValidation.errors.in_qty_per_ip ||
                      error?.in_qty_per_ip?.[0]}
                  </FormFeedback>
                ) : null}
              </div>
            </Col>
          </Row>

          <Row>
            <Col>
              <div className="mb-3">
                <Label className="form-label">Lost Comment</Label>
                <Input
                  name="lost_comment"
                  placeholder="Comment"
                  type="text"
                  onChange={ReceiptValidation.handleChange}
                  onBlur={ReceiptValidation.handleBlur}
                  value={ReceiptValidation.values.lost_comment || ""}
                  onKeyUp={onUNFPAQuantityChange}
                  disabled={disableField({})}
                  invalid={
                    (ReceiptValidation.touched.lost_comment &&
                      ReceiptValidation.errors.lost_comment) ||
                    error?.lost_comment?.[0]
                      ? true
                      : false
                  }
                />
                {(ReceiptValidation.touched.lost_comment &&
                  ReceiptValidation.errors.lost_comment) ||
                error?.lost_comment?.[0] ? (
                  <FormFeedback type="invalid">
                    {ReceiptValidation.errors.lost_comment ||
                      error?.lost_comment?.[0]}
                  </FormFeedback>
                ) : null}
              </div>
            </Col>
            <Col>
              <div className="mb-3">
                <Label className="form-label">Lost Quantity</Label>
                <Input
                  name="lost_quantity"
                  placeholder="Lost Quantity"
                  type="number"
                  min={0}
                  onWheel={e => e.target.blur()}
                  onKeyUp={e => {
                    setLostQtyError(null)
                    if (checkForMinusNumber(e)) {
                      e.target.value = 0
                      return
                    }
                    const qty = ReceiptValidation.values.in_qty_per_ip
                    if (qty < e.target.value) {
                      e.target.value = 0
                      setLostQtyError("Lost Qty Should not greater then IP Qty")
                      return
                    }
                  }}
                  disabled={disableField({})}
                  onChange={ReceiptValidation.handleChange}
                  onBlur={ReceiptValidation.handleBlur}
                  value={ReceiptValidation.values.lost_quantity}
                  invalid={
                    (ReceiptValidation.touched.lost_quantity &&
                      ReceiptValidation.errors.lost_quantity) ||
                    error?.lost_quantity?.[0]
                      ? true
                      : false
                  }
                />
                {(ReceiptValidation.touched.lost_quantity &&
                  ReceiptValidation.errors.lost_quantity) ||
                error?.lost_quantity?.[0] ? (
                  <FormFeedback type="invalid">
                    {ReceiptValidation.errors.lost_quantity ||
                      error?.lost_quantity?.[0]}
                  </FormFeedback>
                ) : null}
                {lostQtyError && (
                  <p className="text-danger" style={{ fontSize: 12 }}>
                    {lostQtyError}
                  </p>
                )}
              </div>
            </Col>
          </Row>

          <Row>
            <Col>
              <div className="mb-3">
                <Label className="form-label">
                  Total Value <span className="text-danger">*</span>
                </Label>
                <Input
                  name="total_value"
                  placeholder="Total Value"
                  type="text"
                  onChange={ReceiptValidation.handleChange}
                  onBlur={ReceiptValidation.handleBlur}
                  value={ReceiptValidation.values.total_value || ""}
                  readOnly={true}
                  invalid={
                    (ReceiptValidation.touched.total_value &&
                      ReceiptValidation.errors.total_value) ||
                    error?.total_value?.[0]
                      ? true
                      : false
                  }
                />
                {(ReceiptValidation.touched.total_value &&
                  ReceiptValidation.errors.total_value) ||
                error?.total_value?.[0] ? (
                  <FormFeedback type="invalid">
                    {ReceiptValidation.errors.total_value ||
                      error?.total_value?.[0]}
                  </FormFeedback>
                ) : null}
              </div>
            </Col>
            <Col>
              <div className="mb-3">
                <Label className="form-label">
                  Submission Date <span className="text-danger">*</span>
                </Label>
                <Flatpickr
                  ref={submissionDateRef}
                  disabled={disableField({})}
                  className="form-control d-block"
                  placeholder="dd M,yyyy"
                  options={{
                    altInput: true,
                    altFormat: "Y-m-d",
                    dateFormat: "Y-m-d",
                    defaultDate: ReceiptValidation.values.date,
                    maxDate: moment().format("YYYY-MM-DD"),
                  }}
                  onChange={value => {
                    ReceiptValidation.setFieldValue(
                      "date",
                      moment(value[0]).format("YYYY-MM-DD")
                    )
                  }}
                />
                {(ReceiptValidation.touched.date &&
                  ReceiptValidation.errors.date) ||
                error?.date ? (
                  <p className="text-danger" style={{ fontSize: "80%" }}>
                    {ReceiptValidation.errors.date || error?.date}
                  </p>
                ) : null}
              </div>
            </Col>
          </Row>

          <div className="d-flex flex-wrap gap-2">
            <Button
              type="submit"
              color="primary"
              disabled={loading || draftLoading}
            >
              {draftLoading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Saving Draft...
                </>
              ) : loading ? (
                "Please Wait..."
              ) : formMode === "Add" ? (
                "Save as Draft"
              ) : (
                "Update"
              )}
            </Button>
          </div>
        </Form>

        {formMode === "Add" && (
          <React.Fragment>
            {/* Render grouped drafts */}
            {groupedDrafts.length > 0
              ? groupedDrafts.map((group, index) => (
                  <ReceiptsSubmitedList
                    key={group.item_id || index}
                    data={group.drafts}
                    loading={draftsLoading}
                    title={`${group.item_name} - ${group.ip_item_description}`}
                    onDeleteDraft={onDeleteDraft}
                    onDeleteManyDrafts={onDeleteManyDrafts}
                  />
                ))
              : !draftsLoading && (
                  <Card className="mt-4">
                    <CardBody className="text-center text-muted">
                      <i className="bx bx-package display-4" />
                      <h5 className="mt-2">No Draft Items</h5>
                      <p>Start by adding items using the form above.</p>
                    </CardBody>
                  </Card>
                )}

            {/* Save All Drafts Button */}
            <div className="d-flex flex-wrap gap-2 mt-3">
              <Button
                type="button"
                onClick={saveAllHandler}
                color="success"
                disabled={loading || drafts.length === 0}
              >
                {loading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Submitting...
                  </>
                ) : (
                  `Save All Drafts (${drafts.length})`
                )}
              </Button>
            </div>
          </React.Fragment>
        )}
      </CardBody>
    </Card>
  )
}

export default ProviderInForm
